import React, { useRef, useMemo, useImperativeHandle, forwardRef } from "react";
import ReactECharts from "echarts-for-react";
import { jsPDF } from "jspdf";

const VulnerabilityChartDisplay = forwardRef(({ chartType, data }, ref) => {
  const chartRef = useRef(null);

  const severityColors = {
    5: " #e53e3e",
    4: " #dd6b20",
    3: " #d69e2e",
    2: " #38a169",
    1: " #4299e1",
  };

  const severityLabels = {
    5: "Critical",
    4: "Severe",
    3: "Moderate",
    2: "Low",
    1: "Info",
  };

  const getColor = (severity) => severityColors[severity] || "#a0aec0";
  const getLabel = (severity) => severityLabels[severity] || "Vulnerability";

  const chartOptions = useMemo(() => {
    const severityLevels = ["Critical", "Severe", "Moderate", "Low", "Info"];
    const severityMap = {
      Info: 1,
      Low: 2,
      Moderate: 3,
      Severe: 4,
      Critical: 5,
    };

    // Initialize count map
    const severityCounts = severityLevels.reduce((acc, level) => {
      acc[level] = 0;
      return acc;
    }, {});

    const topVulnerability = {};

    // Aggregate counts by severity
    data.forEach((item) => {
      const label = item.severityLabel || "Unknown";
      if (severityCounts[label] !== undefined) {
        severityCounts[label] += 1;
        topVulnerability[label] = {};
      }
    });

    const categories = severityLevels;
    const values = categories.map((label) => severityCounts[label]);
    const colors = categories.map((label) => getColor(severityMap[label]));

    const tooltip = {
      trigger: "axis",
      formatter: (params) => {
        const item = params[0];
        return `<strong>Vulnerability: ${item.vulnerability}</strong>`;
      },
    };

    const baseAxes = {
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          rotate: 0,
        },
      },
      yAxis: {
        type: "value",
        name: "Count",
      },
    };

    const baseGrid = {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    };

    const baseSeries = (type) => ({
      name: "Submissions",
      type,
      data: values.map((v, i) => ({
        value: v,
        itemStyle: {
          color: colors[i],
        },
      })),
    });

    switch (chartType) {
      case "bar":
      case "line": {
        const severityLevels = [
          "Critical",
          "Severe",
          "Moderate",
          "Low",
          "Info",
        ];
        const severityMap = {
          Info: 1,
          Low: 2,
          Moderate: 3,
          Severe: 4,
          Critical: 5,
        };

        // Count and track top vulnerability per severity
        const severityCounts = {};
const topVulns = {};

// Initialize with zero values for known severity levels
severityLevels.forEach((label) => {
  severityCounts[label] = 0;
  topVulns[label] = {};
});

if (Array.isArray(data)) {
  data.forEach((item) => {
    try {
      const rawSeverity = item?.severity;
      const label = getLabel(rawSeverity); // This maps severity number to label (e.g., 5 -> "Critical")
      const normalizedLabel = severityLevels.includes(label) ? label : 'Low'; // Fallback to 'Low' if not matched

      const vuln = item?.vulnerability?.trim() || "Unknown";

      // Initialize bucket if needed
      if (!topVulns[normalizedLabel]) {
        topVulns[normalizedLabel] = {};
      }

      severityCounts[normalizedLabel] += 1;
      topVulns[normalizedLabel][vuln] = (topVulns[normalizedLabel][vuln] || 0) + 1;

    } catch (err) {
      console.error("Error processing item:", err, item);
    }
  });
} else {
  console.error("Expected data to be an array, but got:", data);
}

// Prepare chart values
const values = severityLevels.map((label) => severityCounts[label]);
const colors = severityLevels.map((label) => getColor(severityMap[label]));

// Debugging output
// console.log("Severity Counts:", severityCounts);
// console.log("Top Vulnerabilities:", topVulns);

        
        const tooltip = {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          formatter: function (params) {
            const item = params[0];
            const severity = item.axisValue;
            const count = item.data.value;
            const topVulnMap = topVulns[severity];
            const topVuln =
              Object.entries(topVulnMap).sort((a, b) => b[1] - a[1])[0]?.[0] ||
              "N/A";

            if (topVuln === "N/A") {
              return;
            }
            return `Vulnerability: <strong>${topVuln}</strong>`;
          },
        };

        return {
          tooltip,
          grid: baseGrid,
          xAxis: {
            type: "category",
            data: severityLevels,
          },
          yAxis: {
            type: "value",
            name: "Count",
          },
          series: [
            {
              name: "Submissions",
              type: chartType,
              data: values.map((v, i) => ({
                value: v,
                itemStyle: {
                  color: colors[i],
                },
              })),
              smooth: chartType === "line",
              symbol: "circle",
              symbolSize: 8,
              lineStyle: chartType === "line" ? { width: 3 } : undefined,
              label: {
                show: true,
                position: "top",
              },
            },
          ],
        };
      }

      case "pie":
      case "donut":
        return {
          tooltip,
          legend: {
            orient: "vertical",
            right: 10,
            top: "center",
            type: "scroll",
          },
          series: [
            {
              name: "Vulnerability",
              type: "pie",
              radius: chartType === "donut" ? ["40%", "70%"] : "60%",
              center: ["30%", "50%"],
              data: sorted.map((i) => ({
                name: i.vulnerability,
                value: i.severity,
                itemStyle: {
                  color: getColor(i.severity),
                  borderWidth: 2,
                  borderColor: "#fff",
                  borderRadius: 8,
                },
              })),
              emphasis: {
                itemStyle: {
                  shadowBlur: 0,
                  shadowOffsetX: 0,
                  shadowColor: "rgba(0, 0, 0, 0.5)",
                  borderWidth: 2,
                  borderColor: "#fff",
                },
                label:
                  chartType === "donut"
                    ? {
                        show: true,
                        fontSize: 18,
                        fontWeight: "bold",
                      }
                    : undefined,
              },
              label:
                chartType === "donut"
                  ? { show: false }
                  : { formatter: "{b}: {c} ({d}%)" },
              labelLine: chartType === "donut" ? { show: false } : undefined,
            },
          ],
        };

      case "radar":
        return {
          tooltip: { trigger: "item" },
          legend: { data: ["Severity"] },
          radar: {
            indicator: sorted.map((i) => ({ name: i.vulnerability, max: 5 })),
            shape: "circle",
            splitNumber: 5,
            splitLine: {
              lineStyle: {
                color: [
                  "rgba(238, 197, 102, 0.1)",
                  "rgba(238, 197, 102, 0.2)",
                  "rgba(238, 197, 102, 0.4)",
                  "rgba(238, 197, 102, 0.6)",
                  "rgba(238, 197, 102, 0.8)",
                  "rgba(238, 197, 102, 1)",
                ].reverse(),
              },
            },
            axisLine: { lineStyle: { color: "#000000" } },
          },
          series: [
            {
              name: "Severity",
              type: "radar",
              data: [
                {
                  value: values,
                  name: "Severity",
                  areaStyle: { color: "#7f56d9" },
                  lineStyle: { color: "#c6c9cb", width: 2 },
                  itemStyle: { color: "#67748e" },
                },
              ],
            },
          ],
        };

      case "stacked-area":
        const categoryMap = {};
        const severityLevels = [
          "Info",
          "Low",
          "Moderate",
          "Severe",
          "Critical",
        ];

        // Group counts by category and severity
        data.forEach((item) => {
          const cat = item.vtx_main_category || "Other";
          const sev = item.severity || "Info";
          if (!categoryMap[cat]) {
            categoryMap[cat] = {
              Info: 0,
              Low: 0,
              Moderate: 0,
              Severe: 0,
              Critical: 0,
            };
          }
          categoryMap[cat][sev] += 1;
        });

        const categories = Object.keys(categoryMap);
        const series = severityLevels.map((sev) => ({
          name: sev,
          type: "line",
          stack: "Total",
          areaStyle: {},
          emphasis: {
            focus: "series",
          },
          data: categories.map((cat) => categoryMap[cat][sev] || 0),
        }));

        return {
          tooltip: {
            trigger: "axis",
          },
          legend: {
            top: "bottom",
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "10%",
            containLabel: true,
          },
          xAxis: [
            {
              type: "category",
              boundaryGap: false,
              data: categories,
              axisLabel: {
                rotate: 45,
              },
            },
          ],
          yAxis: [
            {
              type: "value",
            },
          ],
          series,
        };

      case "heatmap": {
        const severityNames = ["Info", "Low", "Medium", "High", "Critical"];
        const vulnNames = [...new Set(sorted.map((i) => i.vulnerability))];
        const heatData = sorted.map((i) => [
          vulnNames.indexOf(i.vulnerability),
          i.severity - 1,
          i.count || 1,
        ]);

        return {
          tooltip: {
            position: "top",
            formatter: ({ value }) => {
              const [x, y, count] = value;
              return `<div><strong>${vulnNames[x]}</strong><br/>Severity: ${severityNames[y]}<br/>Count: ${count}</div>`;
            },
          },
          grid: { height: "70%", top: "10%" },
          xAxis: {
            type: "category",
            data: vulnNames,
            splitArea: { show: true },
            axisLabel: { rotate: 45 },
          },
          yAxis: {
            type: "category",
            data: severityNames,
            splitArea: { show: true },
          },
          visualMap: {
            min: 0,
            max: Math.max(...heatData.map((d) => d[2])),
            calculable: true,
            orient: "horizontal",
            left: "center",
            bottom: "0%",
            inRange: {
              color: ["#f3e8ff", "#d1b3ff", "#b38dfc", "#7f56d9", "#7f56d9"],
            },
          },
          series: [
            {
              name: "Vulnerabilities",
              type: "heatmap",
              data: heatData,
              label: { show: true },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: "rgba(0, 0, 0, 0.5)",
                },
              },
            },
          ],
        };
      }

      default:
        return { tooltip, grid: baseGrid };
    }
  }, [chartType, data]);

  const handleDownload = (format) => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;

    const url = chart.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });

    if (format === "png") {
      const link = document.createElement("a");
      link.download = `vulnerability-${chartType}-chart.png`;
      link.href = url;
      link.click();
    } else if (format === "pdf") {
      const pdf = new jsPDF("landscape", "mm", "a4");
      const { width: pdfW } = pdf.internal.pageSize;
      const img = new Image();
      img.onload = () => {
        const h = (img.height * pdfW) / img.width;
        pdf.addImage(url, "PNG", 0, 0, pdfW, h);
        pdf.save(`vulnerability-${chartType}-chart.pdf`);
      };
      img.src = url;
    }
  };

  useImperativeHandle(ref, () => ({
    getEchartsInstance: () => chartRef.current?.getEchartsInstance(),
    downloadChart: handleDownload,
  }));

  return (
    <div className="w-100" style={{ height: "500px" }}>
      <ReactECharts
        ref={chartRef}
        option={chartOptions}
        style={{ height: "250px", width: "100%" }}
        className="rounded"
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
});

export default VulnerabilityChartDisplay;
